import { ExclamationTriangleIcon, InformationCircleIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { format } from "date-fns";
import _ from "lodash";
import React from "react";
import "react-datepicker/dist/react-datepicker.css";
import { connect } from "react-redux";
import { Actions as ACTIONS } from "../../store/actions/auditLogActions";
import { auditTypes, codeList } from "./AuditTypes";
import Tooltip from "@/tooltip";
import Modal from "@/common/Modal";

const AuditLogModal = ({ modalStatus, item, closeModal, codes, jurisdiction }) => {
	return (
		<Modal
			status={modalStatus}
			close={closeModal}
			title="Audit Log Details"
		>
			<AuditLog
				modalStatus={modalStatus}
				item={item}
				codes={codes}
				jurisdiction={jurisdiction}
			/>
		</Modal>
	);
};

const AuditLog = ({ modalStatus, item, codes, jurisdiction }) => {
	const keyFriendlyNames = auditTypes?.[`${item?.type}`]?.keys;

	const itemRenderer = (item, fieldType, customMap) => {
		if (item instanceof Object && typeof (customMap !== "function"))
			return Object.keys(item)
				.map((key, value) => `${key}: ${value}`)
				.join(", ");
		if (fieldType == "numericCode") return codes?.[`${item}`] || item;
		if (typeof item == "boolean") {
			if (fieldType == "customMap") {
				return item ? customMap[1] : customMap[0];
			} else return item ? "Yes" : "No";
		}
		if (fieldType == "date") {
			try {
				return format(new Date(item), "do MMMM yyyy");
			} catch (error) {
				console.warn(error);
			}
		}
		if (customMap && typeof customMap === "function") return customMap(item);
		else if (customMap && !Array.isArray(customMap)) return customMap[jurisdiction][String(item)];

		if (["undefined", "null"].includes(String(item))) return "";
		return String(item);
	};

	const warnings = item?.issues?.filter((item) => item.type === "warning") || [];
	const errors = item?.issues?.filter((item) => item.type === "error") || [];

	if (!modalStatus) return null;

	return (
		<div
			className="block-content"
			style={{ padding: 20 }}
		>
			<div className="col-xs-6 pl-0">
				<p>
					{_.capitalize(item?.action)} by {item?.userName} on{" "}
					{format(new Date(item?.date), "do MMM yyyy HH:mm")}
					{item?.via ? ` via ${item?.via}` : ""}
				</p>
			</div>
			<div className="clearfix"></div>

			{Object.keys(item?.diff || {})?.length > 0 && (
				<>
					<h4 className="block-title push-10">Difference</h4>
					<div className="table-responsive">
						<div
							id="active-people-table_wrapper"
							className="dataTables_wrapper no-footer"
						>
							<table
								id="active-people-table"
								className="js-table-sections table table-hover"
							>
								<thead style={{ display: "table-header-group" }}>
									<tr>
										<th>FIELD</th>
										<th>FROM</th>
										<th>TO</th>
									</tr>
								</thead>
								<tbody>
									{Object.keys(item.diff)
										.sort((a, b) => {
											const prev =
												keyFriendlyNames?.[`${a.split("__")[0]}`]?.name ||
												keyFriendlyNames?.[`${a.split("__")[0]}`] ||
												a;
											const curr =
												keyFriendlyNames?.[`${b.split("__")[0]}`]?.name ||
												keyFriendlyNames?.[`${b.split("__")[0]}`] ||
												b;

											if (prev > curr) return 1;
											if (curr > prev) return -1;
											return 0;
										})
										.map((key) => {
											const diffItem = item.diff[key];
											const deleted = key.includes("__deleted");
											const added = key.includes("__added");
											const fieldKey = keyFriendlyNames?.[`${key.split("__")[0]}`];
											let keyFriendlyName;

											keyFriendlyName =
												fieldKey || key.replace(`${added ? "__added" : "__deleted"}`, "");
											if (fieldKey?.name) keyFriendlyName = fieldKey?.name;
											if (fieldKey?.[jurisdiction]) keyFriendlyName = fieldKey?.[jurisdiction];

											let fieldType;

											if (fieldKey?.isNumericCode) fieldType = "numericCode";
											if (Array.isArray(fieldKey?.customMap)) fieldType = "customMap";
											if (fieldKey?.date) fieldType = "date";

															// TODO please replace this with something better
															if (keyFriendlyName == "Pay Items") {
																// this is a nested item...
																return (
																	<>
																		<tr key={key}>
																			<td colSpan={3}>
																				<strong>
																					Start{" "}
																					{keyFriendlyName.replace(
																						/\#+\d+\#/g,
																						"",
																					)}
																				</strong>
																			</td>
																		</tr>
																		{Object.keys(diffItem).map((nestedKey) => {
																			const deleted = key.includes("__deleted");
																			const added = key.includes("__added");
																			const fieldKey =
																				keyFriendlyNames?.[
																					`${key.split("__")[0]}`
																				];
																			let keyFriendlyName;

																			return (
																				<tr>
																					<td>{diffItem[nestedKey].name}</td>
																					<td>
																						Input:{" "}
																						{parseFloat(
																							diffItem[nestedKey].input
																								?.__old ?? "",
																						).toFixed(2)}
																						<br />
																						Amount:{" "}
																						{parseFloat(
																							diffItem[nestedKey].amount
																								?.__old ?? "",
																						).toFixed(2)}
																					</td>
																					<td>
																						Input:{" "}
																						{parseFloat(
																							diffItem[nestedKey].input
																								?.__new ?? "",
																						).toFixed(2)}
																						<br />
																						Amount:{" "}
																						{parseFloat(
																							diffItem[nestedKey].amount
																								?.__new ?? "",
																						).toFixed(2)}
																					</td>
																				</tr>
																			);
																		})}
																		<tr key={key}>
																			<td colSpan={3}>
																				<strong>
																					End{" "}
																					{keyFriendlyName.replace(
																						/\#+\d+\#/g,
																						"",
																					)}
																				</strong>
																			</td>
																		</tr>
																	</>
																);
															}

											return (
												<tr key={key}>
													<td>{keyFriendlyName.replace(/\#+\d+\#/g, "")}</td>
													<td>
														{itemRenderer(
															deleted ? diffItem : diffItem?.__old,
															fieldType,
															fieldKey?.customMap,
														)}
													</td>
													<td>
														{itemRenderer(
															added ? diffItem : diffItem?.__new,
															fieldType,
															fieldKey?.customMap,
														)}
													</td>
												</tr>
											);
										})}
								</tbody>
							</table>
						</div>
					</div>
				</>
			)}
			{warnings.length > 0 && (
				<>
					<h4 className="block-title push-10">
						<ExclamationTriangleIcon
							className="text-warning"
							style={{ height: "2rem", verticalAlign: "sub" }}
						/>{" "}
						Warnings
					</h4>
					<div className="table-responsive">
						<div
							id="active-people-table_wrapper"
							className="dataTables_wrapper no-footer"
						>
							<table
								id="active-people-table"
								className="js-table-sections table table-hover"
							>
								<thead style={{ display: "table-header-group" }}>
									<tr>
										<th>Message</th>
										<th>Expected</th>
										<th>Received</th>
									</tr>
								</thead>
								<tbody>
									{warnings.map((warning) => (
										<tr key={warning.message}>
											<td>{warning.message}</td>
											<td>
												{warning.expected}
												{warning.expected === "None" ? (
													<Tooltip
														as="span"
														style={{
															marginLeft: "0.5rem",
														}}
														content="Check your settings if this is not what you expected"
														placement="top"
													>
														<InformationCircleIcon
															style={{
																height: "1.5rem",
																verticalAlign: "sub",
															}}
														/>
													</Tooltip>
												) : null}
											</td>
											<td>{warning.received}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				</>
			)}
			{errors.length > 0 && (
				<>
					<h4 className="block-title push-10">
						<ExclamationTriangleIcon
							className="text-danger"
							style={{ height: "2rem", verticalAlign: "sub" }}
						/>{" "}
						Errors
					</h4>
					<div className="table-responsive">
						<div
							id="active-people-table_wrapper"
							className="dataTables_wrapper no-footer"
						>
							<table
								id="active-people-table"
								className="js-table-sections table table-hover"
							>
								<thead style={{ display: "table-header-group" }}>
									<tr>
										<th>Message</th>
									</tr>
								</thead>
								<tbody>
									{errors.map((error) => (
										<tr key={error.message}>
											<td>{error.message}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				</>
			)}
		</div>
	);
};

const mapStateToProps = (state, ownProps) => {
	const taxes = state?.taxOptions?.taxRates?.reduce(
		(a, v) => ({
			[`${v.id}`]: v.name,
			...a,
		}),
		{},
	);

	const ssc = state?.taxOptions?.socialSecurityStatus?.reduce(
		(a, v) => ({
			[`${v.id}`]: v.name,
			...a,
		}),
		{},
	);

	const payschedules = state?.company.settings.paySchedules?.reduce(
		(a, v) => ({
			[`${v.id}`]: v.name,
			...a,
		}),
		{},
	);

	const leaveTypes = state.leave?.leaveSettings?.leaveTypes?.reduce(
		(a, v) => ({
			[`${v.id}`]: v.name,
			...a,
		}),
		{},
	);
	const pensionGroups = state.pensions?.group?.list?.reduce(
		(a, v) => ({
			[`${v.id}`]: v.name,
			...a,
		}),
		{},
	);

	// console.log('taxes', taxes)

	return {
		jurisdiction: state.company?.settings?.jurisdiction,
		modalStatus: state?.auditLogs && state?.auditLogs?.detailView ? true : false,
		item: state.auditLogs && state.auditLogs.detailView,
		codes: { ...taxes, ...ssc, ...payschedules, ...leaveTypes, ...codeList, ...pensionGroups },
	};
};

const mapDispatchToProps = (dispatch) => ({
	closeModal: (item) => dispatch(ACTIONS.showAuditLogDetail({ show: false })),
});

export default connect(mapStateToProps, mapDispatchToProps)(AuditLogModal);
